import axios from 'axios';
import { ProfileResponse, SaveProfileRequest } from '../types/profile';

interface UploadCVResponse {
  success: boolean;
  data?: any;
  error?: string;
  input_tokens?: number;
  output_tokens?: number;
}

const isDevelopment = process.env.NODE_ENV === 'development';

const LOAD_PROFILE_URL = isDevelopment 
  ? 'http://localhost:3001/api/profile/load'
  : process.env.REACT_APP_LOAD_PROFILE_URL || 'https://document-processor-545273374205.europe-north2.run.app/load';

const SAVE_PROFILE_URL = isDevelopment
  ? 'http://localhost:3001/api/profile/save'
  : process.env.REACT_APP_SAVE_PROFILE_URL || 'https://document-processor-545273374205.europe-north2.run.app/save-version';

const UPLOAD_CV_URL = isDevelopment
  ? 'http://localhost:8080/api/v1/upload-cv'
  : process.env.REACT_APP_UPLOAD_CV_URL || 'https://quota-wrapper-545273374205.europe-north2.run.app/api/v1/upload-cv';

export const profileApi = {
  async loadProfile(): Promise<ProfileResponse> {
    console.log('\n=== CLIENT PROFILE LOAD START ===');
    console.log('🌐 Target URL:', LOAD_PROFILE_URL);
    console.log('🔧 Environment:', isDevelopment ? 'Development' : 'Production');
    console.log('⏰ Request Time:', new Date().toISOString());
    
    try {
      console.log('📤 Making axios GET request...');
      const response = await axios.get<ProfileResponse>(LOAD_PROFILE_URL);
      
      console.log('✅ Request successful!');
      console.log('📊 Response Status:', response.status);
      console.log('📄 Response Headers:', Object.keys(response.headers));
      console.log('📦 Response Data (first 200 chars):', JSON.stringify(response.data).substring(0, 200) + '...');
      console.log('=== CLIENT PROFILE LOAD SUCCESS ===\n');
      
      return response.data;
    } catch (error: any) {
      console.log('❌ Request failed!');
      console.error('📊 Error Status:', error.response?.status);
      console.error('📄 Error Headers:', error.response?.headers ? Object.keys(error.response.headers) : 'None');
      console.error('📦 Error Data:', error.response?.data || 'No response data');
      console.error('🔍 Error Message:', error.message);
      console.error('🔍 Error Code:', error.code);
      console.error('🔍 Full Error:', error);
      
      // Let authentication errors bubble up to be handled by axios interceptor
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('🔐 Authentication error detected - letting axios interceptor handle redirect');
        console.log('=== CLIENT PROFILE LOAD AUTH ERROR ===\n');
        throw error;
      }
      
      console.log('=== CLIENT PROFILE LOAD ERROR ===\n');
      // For other errors, return failure response
      return { success: false };
    }
  },

  async saveProfile(profileData: SaveProfileRequest): Promise<{ success: boolean }> {
    console.log('\n=== CLIENT PROFILE SAVE START ===');
    console.log('🌐 Target URL:', SAVE_PROFILE_URL);
    console.log('🔧 Environment:', isDevelopment ? 'Development' : 'Production');
    console.log('📦 Payload Size:', JSON.stringify(profileData).length, 'characters');
    console.log('⏰ Request Time:', new Date().toISOString());
    
    try {
      console.log('📤 Making axios POST request...');
      const response = await axios.post(SAVE_PROFILE_URL, profileData);
      
      console.log('✅ Save request successful!');
      console.log('📊 Response Status:', response.status);
      console.log('📄 Response Headers:', Object.keys(response.headers));
      console.log('📦 Response Data:', response.data);
      console.log('=== CLIENT PROFILE SAVE SUCCESS ===\n');
      
      return { success: true };
    } catch (error: any) {
      console.log('❌ Save request failed!');
      console.error('📊 Error Status:', error.response?.status);
      console.error('📄 Error Headers:', error.response?.headers ? Object.keys(error.response.headers) : 'None');
      console.error('📦 Error Data:', error.response?.data || 'No response data');
      console.error('🔍 Error Message:', error.message);
      console.error('🔍 Error Code:', error.code);
      console.error('🔍 Full Error:', error);
      
      // Let authentication errors bubble up to be handled by axios interceptor
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('🔐 Authentication error detected - letting axios interceptor handle redirect');
        console.log('=== CLIENT PROFILE SAVE AUTH ERROR ===\n');
        throw error;
      }
      
      console.log('=== CLIENT PROFILE SAVE ERROR ===\n');
      // For other errors, return failure response
      return { success: false };
    }
  },

  async uploadCV(file: File): Promise<UploadCVResponse> {
    console.log('\n=== CLIENT CV UPLOAD START ===');
    console.log('🌐 Target URL:', UPLOAD_CV_URL);
    console.log('🔧 Environment:', isDevelopment ? 'Development' : 'Production');
    console.log('📄 File Name:', file.name);
    console.log('📦 File Size:', file.size, 'bytes');
    console.log('📋 File Type:', file.type);
    console.log('⏰ Request Time:', new Date().toISOString());

    try {
      const formData = new FormData();
      formData.append('cv_file', file);

      console.log('📤 Making axios POST request with multipart/form-data...');
      const response = await axios.post<UploadCVResponse>(UPLOAD_CV_URL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minutes for file processing
      });

      console.log('✅ Upload request successful!');
      console.log('📊 Response Status:', response.status);
      console.log('📄 Response Headers:', Object.keys(response.headers));
      console.log('📦 Response Data:', response.data);
      console.log('=== CLIENT CV UPLOAD SUCCESS ===\n');

      return response.data;
    } catch (error: any) {
      console.log('❌ Upload request failed!');
      console.error('📊 Error Status:', error.response?.status);
      console.error('📄 Error Headers:', error.response?.headers ? Object.keys(error.response.headers) : 'None');
      console.error('📦 Error Data:', error.response?.data || 'No response data');
      console.error('🔍 Error Message:', error.message);
      console.error('🔍 Error Code:', error.code);
      console.error('🔍 Full Error:', error);

      // Handle auth errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Check if it's a quota error (403 with quota message)
        if (error.response?.data?.error?.includes('quota')) {
          console.log('⚠️ Quota error detected - returning error to UI');
          console.log('=== CLIENT CV UPLOAD QUOTA ERROR ===\n');
          return {
            success: false,
            error: error.response.data.error,
          };
        }
        // Other auth errors - let interceptor handle
        console.log('🔐 Authentication error detected - letting axios interceptor handle redirect');
        console.log('=== CLIENT CV UPLOAD AUTH ERROR ===\n');
        throw error;
      }

      console.log('=== CLIENT CV UPLOAD ERROR ===\n');
      // For other errors, return failure response
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to upload CV',
      };
    }
  }
};